import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Paperclip, X, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

// =====================================================================
// Vaste bouwstenen van de app. Regels: docs/08-STIJLGIDS.md.
// Pagina's bouwen hiermee; geen eigen kaarten, koppen of lettergroottes.
// =====================================================================

/** Iconmaten: 16 in tekst en lijsten, 20 in navigatie en knoppen, 24 in onderbalk en lege staten. */
export const ICON = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' } as const;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Telt op naar `value` (300 ms, rustige easing). Respecteert prefers-reduced-motion. */
export function useCountUp(value: number | null | undefined, duration = 300): number | null {
  const [shown, setShown] = useState<number | null>(value ?? null);
  const from = useRef(0);
  useEffect(() => {
    if (value == null) return setShown(null);
    if (reducedMotion()) return setShown(value);
    const start = performance.now();
    const begin = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(begin + (value - begin) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return shown;
}

export function PageHeader({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="min-w-0">
        <h1>{title}</h1>
        {sub && <p className="page-sub">{sub}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Kaart met optionele kop (titel + actie rechts). `flush`: inhoud loopt tot de rand (lijsten, tabellen). */
export function Card({
  title,
  icon: Icon,
  action,
  flush,
  className,
  children,
  style,
}: {
  title?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
  flush?: boolean;
  className?: string;
  children?: ReactNode;
  style?: React.CSSProperties;
}) {
  const head = (title || action) && (
    <div className={flush ? 'card-head' : 'mb-3 flex items-center justify-between gap-3'}>
      {title && (
        <h2 className="flex min-w-0 items-center gap-2">
          {Icon && <Icon className={cn(ICON.sm, 'shrink-0 text-slate-400')} aria-hidden />}
          <span className="truncate">{title}</span>
        </h2>
      )}
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
  return (
    <section className={cn(flush ? 'card-flush' : 'card', 'enter', className)} style={style}>
      {head}
      {children}
    </section>
  );
}

/** Link rechtsboven in een kaart. */
export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-sm text-slate-400 transition-colors duration-fast hover:text-slate-100">
      {children}
      <ChevronRight className={ICON.sm} aria-hidden />
    </Link>
  );
}

type Tone = 'default' | 'money' | 'warn' | 'err' | 'muted';
const TONE: Record<Tone, string> = {
  default: 'text-slate-50',
  money: 'text-money',
  warn: 'text-amber-200',
  err: 'text-red-300',
  muted: 'text-slate-300',
};

/** Kerncijfer: icoon + label, groot getal dat optelt, en een regel eronder. */
export function Kpi({
  icon: Icon,
  label,
  value,
  format,
  text,
  tone = 'default',
  hint,
  rows,
  bar,
  to,
  className,
}: {
  icon?: LucideIcon;
  label: ReactNode;
  value?: number | null;
  format?: (n: number) => string;
  /** In plaats van een getal (bijv. "06-11"). */
  text?: ReactNode;
  tone?: Tone;
  hint?: ReactNode;
  /** Uitsplitsing onder het getal: label links, waarde rechts. */
  rows?: Array<[ReactNode, ReactNode] | false | null | undefined>;
  /** Voortgangsbalk onderaan (bijv. uren tot de overwerkgrens). */
  bar?: { value: number; max: number; over?: boolean };
  to?: string;
  className?: string;
}) {
  const n = useCountUp(value);
  const body = (
    <>
      <div className="t-label flex items-center gap-1.5">
        {Icon && <Icon className={cn(ICON.sm, 'text-slate-500')} aria-hidden />}
        {label}
      </div>
      <div className={cn('t-kpi mt-2', TONE[tone])}>
        {text ?? (n == null ? '–' : format ? format(n) : Math.round(n).toString())}
      </div>
      {hint && <div className="mt-3 border-t border-surface-line/70 pt-2.5 text-xs text-slate-400">{hint}</div>}
      {rows && rows.some(Boolean) && (
        <dl className={cn('space-y-1', hint ? 'mt-2' : 'mt-3 border-t border-surface-line/70 pt-2.5')}>
          {rows.filter((r): r is [ReactNode, ReactNode] => !!r).map(([k, v], i) => (
            <div key={i} className="flex items-baseline justify-between gap-3 text-xs">
              <dt className="min-w-0 text-slate-400">{k}</dt>
              <dd className="t-num shrink-0 text-slate-200">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {bar && (
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-slate-700/60">
          <div
            className={cn('h-full rounded-full transition-[width] duration-slow ease-calm', bar.over ? 'bg-brand-400' : 'bg-slate-300')}
            style={{ width: `${Math.min(100, (bar.value / bar.max) * 100)}%` }}
          />
        </div>
      )}
    </>
  );
  const cls = cn('card enter block min-w-0', to && 'transition-colors duration-fast ease-calm hover:border-slate-600', className);
  return to ? (
    <Link to={to} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** Rij in een lijst: icoon, titel + meta, waarde rechts, pijltje als hij ergens heen gaat. */
export function ListRow({
  icon: Icon,
  iconClass,
  title,
  meta,
  value,
  valueClass,
  to,
  onClick,
  i,
}: {
  icon?: LucideIcon;
  iconClass?: string;
  title: ReactNode;
  meta?: ReactNode;
  value?: ReactNode;
  valueClass?: string;
  to?: string;
  onClick?: () => void;
  /** Volgnummer voor het zachte inladen. */
  i?: number;
}) {
  const inner = (
    <>
      {Icon && <Icon className={cn(ICON.sm, 'shrink-0 text-slate-400', iconClass)} aria-hidden />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-slate-100">{title}</div>
        {meta && <div className="truncate text-xs text-slate-400">{meta}</div>}
      </div>
      {value != null && <div className={cn('shrink-0 text-sm tabular-nums text-slate-100', valueClass)}>{value}</div>}
      {(to || onClick) && <ChevronRight className={cn(ICON.sm, 'shrink-0 text-slate-500')} aria-hidden />}
    </>
  );
  const style = i != null ? ({ '--i': i } as React.CSSProperties) : undefined;
  if (to)
    return (
      <Link to={to} className="row-link enter" style={style}>
        {inner}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" onClick={onClick} className="row-link enter" style={style}>
        {inner}
      </button>
    );
  return (
    <div className="row enter" style={style}>
      {inner}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} aria-hidden />;
}

/** Plaatshouder voor een pagina die laadt: kop, rij kerncijfers, twee blokken. */
export function PageSkeleton({ kpis = 3 }: { kpis?: number }) {
  return (
    <div className="page" aria-busy="true" aria-label="Laden">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className={cn('grid gap-3 sm:gap-4', kpis === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3')}>
        {Array.from({ length: kpis }).map((_, i) => (
          <div key={i} className="card space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-28" />
          </div>
        ))}
      </div>
      <div className="card space-y-3">
        <Skeleton className="h-4 w-32" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

/** Openklapper: kop altijd zichtbaar, details pas na een klik. */
export function Disclosure({
  title,
  meta,
  right,
  icon: Icon,
  defaultOpen = false,
  open: openProp,
  onToggle,
  children,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  right?: ReactNode;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  /** Van buitenaf gestuurd (bv. accordeon waarin er één tegelijk open is). */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (v: boolean) => (onToggle ? onToggle(v) : setOpenState(v));
  // Inhoud pas laden bij de eerste keer openen, daarna laten staan zodat dichtklappen kan animeren.
  const [geweest, setGeweest] = useState(open);
  useEffect(() => {
    if (open) setGeweest(true);
  }, [open]);
  const id = useId();
  return (
    <div className={className}>
      <button type="button" className="row-link" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        {Icon && <Icon className={cn(ICON.sm, 'shrink-0 text-slate-400')} aria-hidden />}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-100">{title}</div>
          {meta && <div className="truncate text-xs text-slate-400">{meta}</div>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
        <ChevronDown
          className={cn(ICON.sm, 'shrink-0 text-slate-500 transition-transform duration-base ease-calm', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      <div
        id={id}
        className={cn('grid transition-[grid-template-rows,opacity] duration-slow ease-calm', open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
        aria-hidden={!open}
        {...(open ? {} : { inert: '' })}
      >
        <div className="min-h-0 overflow-hidden">{geweest && <div className="px-4 pb-4 sm:px-5">{children}</div>}</div>
      </div>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  text,
  action,
}: {
  icon: LucideIcon;
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      <Icon className={cn(ICON.lg, 'text-slate-500')} aria-hidden />
      <div className="mt-3 text-sm font-medium text-slate-200">{title}</div>
      {text && <p className="mt-1 max-w-sm text-sm text-slate-400">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Positie van het actieve item in een rij, voor een indicator die meeschuift (tabs, segmented). */
function useIndicator(active: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const meet = () => {
      const item = el.querySelectorAll<HTMLElement>('[data-ind]')[active];
      setPos(item ? { left: item.offsetLeft, width: item.offsetWidth } : null);
    };
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);
  return { ref, pos };
}

/** Tabs met een streep onder het actieve tabblad; scrollen horizontaal op mobiel. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
}: {
  items: Array<{ value: T; label: ReactNode; icon?: LucideIcon; count?: number }>;
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  const { ref, pos } = useIndicator(items.findIndex((t) => t.value === value));
  return (
    <div ref={ref} role="tablist" className={cn('tabs relative', className)}>
      {items.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          data-ind
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cn('tab', value === t.value && 'tab-active')}
        >
          {t.icon && <t.icon className={ICON.sm} aria-hidden />}
          {t.label}
          {t.count != null && t.count > 0 && <span className="badge-count">{t.count}</span>}
        </button>
      ))}
      {pos && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-slate-100 transition-[left,width] duration-slow ease-calm"
          style={{ left: pos.left, width: pos.width }}
        />
      )}
    </div>
  );
}

/** Knop met alleen een icoon: altijd een label (tooltip + schermlezer). */
export function IconButton({
  icon: Icon,
  label,
  onClick,
  className,
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button type="button" className={cn('icon-btn', className)} onClick={onClick} aria-label={label} title={label} disabled={disabled}>
      <Icon className={ICON.md} aria-hidden />
    </button>
  );
}

/**
 * Paneel: schuift op desktop van rechts in, op mobiel van onderen. Sluit met Esc of een klik ernaast.
 */
export function Sheet({
  open,
  onClose,
  title,
  sub,
  footer,
  children,
  wide,
  modal = true,
  vasteHoogte,
  breed,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /** Vanaf tablet een gecentreerd venster (standaard); false = paneel aan de zijkant. */
  modal?: boolean;
  /** Vaste hoogte, zodat het venster niet verspringt als de inhoud wisselt (bv. bij onderdelen). */
  vasteHoogte?: boolean;
  /** Extra breed venster (twee kolommen op desktop), voor formulieren met veel velden. */
  breed?: boolean;
}) {
  // Sluiten speelt eerst de uitgaande animatie af en roept dan onClose aan.
  const [weg, setWeg] = useState(false);
  const sluit = useCallback(() => {
    if (weg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return onClose();
    setWeg(true);
    window.setTimeout(() => {
      setWeg(false);
      onClose();
    }, 180);
  }, [weg, onClose]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && sluit();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, sluit]);
  if (!open) return null;
  return createPortal(
    <div
      className={cn('fixed inset-0 z-50 flex items-end', modal ? 'sm:items-center sm:justify-center sm:p-6' : 'sm:items-stretch sm:justify-end')}
      role="dialog"
      aria-modal="true"
    >
      <div className={cn('absolute inset-0 bg-black/60', weg ? 'animate-overlay-out' : 'animate-overlay-in')} onClick={sluit} />
      <div
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col rounded-t-2xl border-t border-surface-line bg-surface-raised',
          modal
            ? cn('sm:rounded-2xl sm:border sm:shadow-2xl', vasteHoogte ? 'sm:h-[min(85vh,780px)] sm:max-h-none' : 'sm:max-h-[92vh]')
            : 'sm:max-h-none sm:h-full sm:rounded-none sm:border-l sm:border-t-0',
          breed ? 'sm:max-w-2xl lg:max-w-5xl' : wide ? 'sm:max-w-2xl' : 'sm:max-w-md',
          // In en uit: elk een eigen animatie, zodat sluiten echt afspeelt.
          weg
            ? cn('animate-sheet-down', modal ? 'sm:animate-modal-out' : 'sm:animate-sheet-right-out')
            : cn('animate-sheet-up', modal ? 'sm:animate-modal-in' : 'sm:animate-sheet-right')
        )}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <span className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-slate-600 sm:hidden" aria-hidden />
        <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-5 sm:px-5">
          <div className="min-w-0">
            <h2>{title}</h2>
            {sub && <p className="mt-0.5 text-sm text-slate-400">{sub}</p>}
          </div>
          <IconButton icon={X} label="Sluiten" onClick={sluit} className="-mr-2 -mt-2" />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-5 sm:px-5">{children}</div>
        {footer && <div className="border-t border-surface-line px-4 py-3 sm:px-5">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/** Bestand kiezen in het Nederlands (de browserknop zegt "Choose File"). */
export function FileInput({
  accept,
  file,
  onChange,
  label = 'Bestand kiezen',
}: {
  accept?: string;
  file: File | null;
  onChange: (f: File | null) => void;
  label?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="flex min-w-0 items-center gap-3">
      <button type="button" className="btn-secondary shrink-0" onClick={() => ref.current?.click()}>
        <Paperclip className={ICON.sm} aria-hidden />
        {label}
      </button>
      <span className={cn('truncate text-sm', file ? 'text-slate-200' : 'text-slate-500')}>{file ? file.name : 'Geen bestand gekozen'}</span>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
    </div>
  );
}

/** Melding na een actie: groen bij gelukt, rood bij fout. */
export function Notice({ tone, children }: { tone: 'ok' | 'err' | 'warn' | 'info'; children: ReactNode }) {
  const cls = {
    ok: 'border-money/30 bg-money/10 text-money',
    err: 'border-red-500/30 bg-red-500/10 text-red-200',
    warn: 'border-amber-500/30 bg-amber-500/10 text-amber-100',
    info: 'border-surface-line bg-surface-raised text-slate-300',
  }[tone];
  return <div className={cn('animate-enter rounded-lg border px-3 py-2 text-sm', cls)}>{children}</div>;
}

/** Kleine keuzeschakelaar, bijvoorbeeld Bruto/Netto. */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  const { ref, pos } = useIndicator(items.findIndex((it) => it.value === value));
  return (
    <div ref={ref} role="radiogroup" aria-label={label} className="relative inline-flex rounded-lg border border-surface-line p-0.5">
      {pos && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0.5 top-0.5 rounded-md bg-slate-100 transition-[left,width] duration-slow ease-calm"
          style={{ left: pos.left, width: pos.width }}
        />
      )}
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="radio"
          data-ind
          aria-checked={value === it.value}
          onClick={() => onChange(it.value)}
          className={cn(
            'relative rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-base ease-calm',
            value === it.value ? 'text-slate-900' : 'text-slate-400 hover:text-slate-100'
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
